import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Student,
  StudentCreateInput,
  StudentListFilters,
  StudentListResponse,
  StudentStatus,
  StudentTransitionInput,
  StudentUpdateInput,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase J1 — TanStack Query bindings for `/api/students/*`.
 *
 * Phase J1.b extends with the four write mutations:
 *   - `useCreateStudent`     POST   /api/students/
 *   - `useUpdateStudent`     PATCH  /api/students/{id}
 *   - `useTransitionStudent` POST   /api/students/{id}/transition
 *   - `useDeleteStudent`     DELETE /api/students/{id}     (soft-delete)
 *
 * All mutations invalidate `studentKeys.all` so list + detail + siblings
 * stay consistent. The transition mutation also returns the updated
 * `StudentResponse`, which we `setQueryData` on the detail key so a
 * drawer that's still open reflects the new status without a refetch
 * round-trip.
 *
 * Tenant scoping: the BE derives the tenant from the bearer token; we
 * never pass `X-Tenant-Slug` from this hook (the api-client interceptor
 * does that). `view_students` is the read guard; `manage_students` is
 * the write guard. Both are gated FE-side in the page, not here —
 * mirrors the AssessmentTemplates pattern: the hook only does HTTP,
 * permission denials are surfaced via 403 → toast.
 */
export const studentKeys = {
  all: ["students"] as const,
  list: (filters?: StudentListFilters) => [...studentKeys.all, "list", filters ?? {}] as const,
  detail: (id: string | null) => [...studentKeys.all, "detail", id] as const,
  siblings: (id: string | null) => [...studentKeys.all, "siblings", id] as const,
};

export function buildStudentsQuery(filters?: StudentListFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.status) search.set("status", filters.status);
  if (filters.batch_id) search.set("batch_id", filters.batch_id);
  if (filters.grade) search.set("grade", filters.grade);
  if (filters.section) search.set("section", filters.section);
  if (filters.parent_id) search.set("parent_id", filters.parent_id);
  if (filters.class_teacher_id) search.set("class_teacher_id", filters.class_teacher_id);
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useStudentsList(filters?: StudentListFilters) {
  const qs = buildStudentsQuery(filters);
  return useQuery<StudentListResponse>({
    queryKey: studentKeys.list(filters),
    queryFn: () => api.get<StudentListResponse>(`/api/students/${qs ? `?${qs}` : ""}`),
    staleTime: 15_000,
  });
}

export function useStudent(studentId: string | null) {
  return useQuery<Student>({
    queryKey: studentKeys.detail(studentId),
    queryFn: () => api.get<Student>(`/api/students/${studentId}`),
    enabled: Boolean(studentId),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Phase J1.b — write mutations
// ---------------------------------------------------------------------------

export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation<Student, Error, StudentCreateInput>({
    mutationKey: ["students", "create"],
    mutationFn: (body) => api.post<Student>("/api/students/", body),
    onSuccess: (student) => {
      qc.setQueryData(studentKeys.detail(student.id), student);
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

export interface UseUpdateStudentVars {
  id: string;
  body: StudentUpdateInput;
}

export function useUpdateStudent() {
  const qc = useQueryClient();
  return useMutation<Student, Error, UseUpdateStudentVars>({
    mutationKey: ["students", "update"],
    mutationFn: ({ id, body }) => api.patch<Student>(`/api/students/${id}`, body),
    onSuccess: (student) => {
      qc.setQueryData(studentKeys.detail(student.id), student);
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

export interface UseTransitionStudentVars {
  id: string;
  body: StudentTransitionInput;
}

export function useTransitionStudent() {
  const qc = useQueryClient();
  return useMutation<Student, Error, UseTransitionStudentVars>({
    mutationKey: ["students", "transition"],
    mutationFn: ({ id, body }) => api.post<Student>(`/api/students/${id}/transition`, body),
    onSuccess: (student) => {
      qc.setQueryData(studentKeys.detail(student.id), student);
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

export function useDeleteStudent() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationKey: ["students", "delete"],
    mutationFn: (id) => api.delete<void>(`/api/students/${id}`),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: studentKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

/**
 * Resolved status options for the transition dialog dropdown.
 *
 * NOTE: the active row's current status is filtered out at render time
 * — there's nothing to transition from `active` → `active`.
 */
export const STUDENT_TRANSITION_STATUSES: ReadonlyArray<{
  value: StudentStatus;
  label: string;
  description: string;
  terminal: boolean;
}> = [
  {
    value: "active",
    label: "Active",
    description: "Currently enrolled and attending.",
    terminal: false,
  },
  {
    value: "paused",
    label: "Paused",
    description: "Temporarily on hold (medical leave, gap year, etc.).",
    terminal: false,
  },
  {
    value: "graduated",
    label: "Graduated",
    description: "Completed the program. Becomes an alumnus.",
    terminal: true,
  },
  {
    value: "withdrawn",
    label: "Withdrawn",
    description: "Student or family withdrew the enrolment.",
    terminal: true,
  },
  {
    value: "transferred",
    label: "Transferred",
    description: "Moved to a different institution.",
    terminal: true,
  },
];
